
import { defineComponent, ref, computed, watch, nextTick, PropType } from "vue";
import VueMultiselect from "vue-multiselect";
import { IDataTableFilterOptionOptions } from "@/interfaces/iPageModel";
import { TranslationService } from "@/core/services/TranslationService";

export default defineComponent({
  name: "vz-lookup",
  inheritAttrs: false,
  props: {
    options: {
      type: Array as PropType<IDataTableFilterOptionOptions[]>,
      required: true,
      default: () => [],
    },
    modelValue: {
      type: Array as PropType<(string | null)[]>,
      default: () => [],
    },
  },
  emits: ["update:modelValue", "input"],
  components: { VueMultiselect },
  setup(props, { emit }) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const selectAll = t("common.selectAll");
    const removeAll = t("common.removeAll");

    const selectedItems = ref<any[]>([]);
    const multiselect = ref<any>(null);

    const getItemValues = (items: any[]) =>
      items.map((item) => String(item.value)).sort();

    // Sync internal selectedItems with external modelValue changes (only if values differ)
    watch(
      () => props.modelValue,
      (newVal) => {
        const nonNullValues = (newVal || []).filter(
          (v): v is string => v !== null
        );
        const newSelected = realOptions.value.filter((option) =>
          nonNullValues.includes(String(option.value))
        );
        const currentValues = getItemValues(selectedItems.value);
        const newValues = [...nonNullValues].sort();
        if (currentValues.join() !== newValues.join()) {
          selectedItems.value = newSelected;
        }
      }
    );

    const filteredItems = computed(() =>
      (props.options || []).map((option) => ({
        ...option,
        value: option.value ?? "",
        label: option.label,
      }))
    );

    const realOptions = computed(() =>
      filteredItems.value.filter((option) => option.value !== "-1")
    );

    const selectAllOption = { label: "Select All", value: "-1" };
    const removeAllOption = { label: "Remove All", value: "remove_all" };

    const isAllSelected = computed(() => {
      return selectedItems.value?.length === realOptions.value.length;
    });

    const computedOptions = computed(() => {
      if (realOptions.value.length === 0) {
        return [];
      }
      return isAllSelected.value
        ? [removeAllOption, ...realOptions.value]
        : [selectAllOption, ...realOptions.value];
    });

    const limitText = (count: number) => `+ ${count}`;

    // Watch selectedItems for changes (select/remove) to handle logic and emit
    watch(
      selectedItems,
      (newSelected) => {
        let currentSelected = [...newSelected];
        let hasSelectAll = currentSelected.some((item) => item.value === "-1");
        let hasRemoveAll = currentSelected.some(
          (item) => item.value === "remove_all"
        );

        if (hasSelectAll) {
          const validItems = realOptions.value;
          selectedItems.value = [...validItems];
          currentSelected = [...validItems];
          multiselect.value?.deactivate();
        } else if (hasRemoveAll) {
          selectedItems.value = [];
          currentSelected = [];
          multiselect.value?.deactivate();
        } else {
          multiselect.value?.deactivate();
        }

        const values = currentSelected
          .map((item) => item.value)
          .filter((v) => v !== "-1" && v !== "remove_all");

        emit("input", values);
        emit("update:modelValue", values);
      },
      { deep: true }
    );

    const handleRemove = (removedItem: any) => {
      if (removedItem.value === "-1" || removedItem.value === "remove_all")
        return;
      multiselect.value?.deactivate();
      // Watch will handle emit
    };

    // Initial sync
    nextTick(() => {
      const nonNullInitialValues = props.modelValue.filter(
        (v): v is string => v !== null
      );
      const newSelected = realOptions.value.filter((option) =>
        nonNullInitialValues.includes(String(option.value))
      );
      const currentValues = getItemValues(selectedItems.value);
      const newValues = [...nonNullInitialValues].sort();
      if (currentValues.join() !== newValues.join()) {
        selectedItems.value = newSelected;
      }
    });

    return {
      t,
      selectAll,
      removeAll,
      selectedItems,
      multiselect,
      filteredItems,
      realOptions,
      computedOptions,
      isAllSelected,
      limitText,
      handleRemove,
    };
  },
});
